import { generateTempInviteCode, generateTempLane } from '../tempChat/tempChatControl.ts'

export type TempChatStatus = 'pending_outgoing' | 'pending_incoming' | 'active'

export interface TempChatSession {
  contactId: string
  chatKey: string
  peerSessionToken: string
  expiresAt: number
  status: TempChatStatus
  durationMs?: number
  fromNickname?: string
  inviteCode?: string
}

const STORAGE_KEY = 'zion_temp_chats'

const listeners = new Set<() => void>()

function loadAll(): TempChatSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as TempChatSession[]
    if (!Array.isArray(parsed)) return []
    const now = Date.now()
    return parsed
      .filter(
        (s) =>
          s &&
          s.chatKey?.trim() &&
          s.contactId?.trim() &&
          s.peerSessionToken?.trim() &&
          s.expiresAt > now,
      )
      .map((s) => ({
        ...s,
        status: s.status ?? 'active',
      }))
  } catch {
    return []
  }
}

function saveAll(sessions: TempChatSession[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions))
  } catch {
    /* ignore */
  }
}

function notify(): void {
  for (const fn of listeners) fn()
}

export function onTempChatsChanged(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function listTempChats(): TempChatSession[] {
  return loadAll()
}

export function listActiveTempChats(): TempChatSession[] {
  return loadAll().filter((s) => s.status === 'active')
}

export function listPendingIncomingTempChats(): TempChatSession[] {
  return loadAll().filter((s) => s.status === 'pending_incoming')
}

export function listPendingOutgoingTempChats(): TempChatSession[] {
  return loadAll().filter((s) => s.status === 'pending_outgoing')
}

export function getTempChatByContact(contactId: string): TempChatSession | null {
  const id = contactId.trim()
  return loadAll().find((s) => s.contactId === id) ?? null
}

export function findActiveTempForContact(contactId: string): TempChatSession | null {
  const id = contactId.trim()
  const active = loadAll().filter((s) => s.contactId === id && s.status === 'active')
  if (!active.length) return null
  return active.sort((a, b) => b.expiresAt - a.expiresAt)[0] ?? null
}

/** Allinea lane wire (temp:xxx o legacy nick_nick) alla sessione temp del contatto. */
export function resolveTempChatKey(contactId: string, incomingLane: string): string {
  const cid = contactId.trim()
  const lane = incomingLane.trim().toLowerCase()
  if (lane) {
    const byLane = getTempChatByKey(lane)
    if (byLane && byLane.contactId === cid) return byLane.chatKey
    if (!lane.startsWith('temp:')) {
      const withPrefix = `temp:${lane.replace(/^temp:/, '')}`
      const byPref = getTempChatByKey(withPrefix)
      if (byPref && byPref.contactId === cid) return byPref.chatKey
    }
  }
  const forContact = loadAll().filter((s) => s.contactId === cid)
  const pending =
    forContact.find((s) => s.status === 'pending_outgoing') ??
    forContact.find((s) => s.status === 'pending_incoming')
  if (pending) return pending.chatKey
  const active = findActiveTempForContact(cid)
  if (active) return active.chatKey
  return lane || forContact[0]?.chatKey || ''
}

export function sessionsEqual(a: string, b: string): boolean {
  const x = a.trim().toLowerCase()
  const y = b.trim().toLowerCase()
  return !!x && !!y && x === y
}

export function findPendingOutgoingTempForPeer(peerSessionToken: string): TempChatSession | null {
  const peer = peerSessionToken.trim().toLowerCase()
  if (!peer) return null
  return (
    loadAll().find(
      (s) =>
        s.status === 'pending_outgoing' &&
        s.peerSessionToken.trim().toLowerCase() === peer,
    ) ?? null
  )
}

export function findPendingOutgoingByLane(lane: string): TempChatSession | null {
  const key = lane.trim().toLowerCase()
  if (!key) return null
  return (
    loadAll().find(
      (s) =>
        s.status === 'pending_outgoing' &&
        s.chatKey.trim().toLowerCase() === key,
    ) ?? null
  )
}

export function getTempChatByKey(chatKey: string): TempChatSession | null {
  const key = chatKey.trim().toLowerCase()
  if (!key) return null
  return loadAll().find((s) => s.chatKey.trim().toLowerCase() === key) ?? null
}

export function upsertTempChat(session: TempChatSession): void {
  const all = loadAll().filter((s) => s.chatKey !== session.chatKey)
  all.push(session)
  saveAll(all)
  notify()
}

export function removeTempChat(chatKey: string): void {
  const key = chatKey.trim()
  const next = loadAll().filter((s) => s.chatKey !== key)
  saveAll(next)
  notify()
}

export function removeTempChatsForContact(contactId: string): void {
  const id = contactId.trim()
  const next = loadAll().filter((s) => s.contactId !== id)
  saveAll(next)
  notify()
}

export function clearAllTempChats(): void {
  saveAll([])
  notify()
}

export function buildTempSession(
  contactId: string,
  _mySessionToken: string,
  peerSessionToken: string,
  expiresAt: number,
  durationMs?: number,
): TempChatSession {
  return {
    contactId: contactId.trim(),
    chatKey: generateTempLane(),
    peerSessionToken: peerSessionToken.trim(),
    expiresAt,
    status: 'pending_outgoing',
    durationMs,
    inviteCode: generateTempInviteCode(),
  }
}
